import {
  CloseRounded,
  ContentCopyRounded,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined,
  RestartAltRounded,
  SwapHorizRounded,
} from '@mui/icons-material';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import { Box, IconButton, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import React from 'react';
import ReactGA from 'react-ga4';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { DispatchDetailPanel, DispatchSelection, GeoUpdatePayload } from './DispatchDetailPanel';

export type DetailPanelPlacement = 'anchor' | 'mapTopRight';

/** クリック位置に寄せる配置に使う矩形。placement='anchor' のときだけ参照する */
export type DetailPanelClickRect = {
  top: number;
  left: number;
  right: number;
  height: number;
};

/** 「他車両へ配車」の移動先候補。除外や並びは呼び出し側で決める */
export type DetailPanelMoveTarget = {
  rowId: string;
  label: string;
  subLabel: string;
};

export interface DetailPanelShellProps {
  selection: DispatchSelection;
  placement: DetailPanelPlacement;
  /** placement='anchor' のときだけ使う */
  clickRect?: DetailPanelClickRect | null;
  screenName: string;
  hasDuplicate?: boolean;
  geoAlert?: GeoAlertInfo;
  onGeoUpdate?: (payload: GeoUpdatePayload) => void;
  /**
   * 「他車両へ配車」の Popover の anchor。hook が持つ state をそのまま受け渡す。
   * shell 内で useState すると hook 側の scope 破棄が効かない。
   */
  moveMenuAnchor: HTMLElement | null;
  onChangeMoveMenuAnchor: (el: HTMLElement | null) => void;
  onClose: () => void;
  /** 未指定ならそのボタンを描かない */
  onEditOrder?: (shipmentOrderId: number) => void;
  onEditLane?: (laneId: number) => void;
  onMoveToVehicle?: (targetRowId: string) => void;
  onResetToUnassigned?: () => void;
  onSplit?: () => void;
  onDelete?: () => void;
  /** 案件のコピーを作る。未指定ならボタン非表示（未割当のときだけ出る） */
  onCopy?: () => void;
  /** 「他車両へ配車」の移動先候補。onMoveToVehicle があるときだけ使う */
  moveTargets?: DetailPanelMoveTarget[];
  /** 伝票 ID の解決。unassigned のとき「案件編集」の有効/無効に使う */
  resolveShipmentOrderId?: (itemId: string) => number | null;
  /**
   * 指定時は器の書き込み系ボタン（未割り当てに戻す・案件を削除・伝票を分割・
   * 案件編集/レーン管理を開く）を disabled にし、素の title 属性で理由を出す（Task 10）。
   * 「他車両へ配車」は自動配車ダイアログを開くだけで mutation を伴わないため対象外。
   */
  disabledReason?: string;
}

const PANEL_WIDTH = 380;
const ANCHOR_GAP = 8;

/** クリックされたセルの右（入らなければ左）に、縦中央を合わせて置く */
export function buildAnchorPositionStyle(clickRect: DetailPanelClickRect | null | undefined): React.CSSProperties {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const posStyle: React.CSSProperties = {};
  if (clickRect) {
    const cellCenterY = clickRect.top + clickRect.height / 2;
    const spaceRight = viewportWidth - clickRect.right;
    const showOnLeft = spaceRight < PANEL_WIDTH + ANCHOR_GAP + 40;
    if (showOnLeft) {
      posStyle.right = viewportWidth - clickRect.left + ANCHOR_GAP;
    } else {
      posStyle.left = clickRect.right + ANCHOR_GAP;
    }
    const maxHeight = Math.min(viewportHeight * 0.8, 600);
    let top = cellCenterY - maxHeight / 2;
    top = Math.max(16, Math.min(top, viewportHeight - maxHeight - 16));
    posStyle.top = top;
    posStyle.maxHeight = maxHeight;
  } else {
    posStyle.right = 360;
    posStyle.top = '50%';
    posStyle.transform = 'translateY(-50%)';
    posStyle.maxHeight = '80vh';
  }
  return posStyle;
}

export function DetailPanelShell({
  selection,
  placement,
  clickRect,
  screenName,
  hasDuplicate,
  geoAlert,
  onGeoUpdate,
  moveMenuAnchor,
  onChangeMoveMenuAnchor,
  onClose,
  onEditOrder,
  onEditLane,
  onMoveToVehicle,
  onResetToUnassigned,
  onSplit,
  onDelete,
  onCopy,
  moveTargets,
  resolveShipmentOrderId,
  disabledReason,
}: DetailPanelShellProps) {
  if (!selection) return null;

  const isDeliveryCard = selection.source === 'vehicle' && selection.card.type === 'delivery';
  const unassignedItem = selection.source === 'unassigned' ? selection.item : null;
  /**
   * 「地点単位」でまとめた未割当は、レーン配下の伝票をまとめた 1 件。案件 1 件に対する操作
   * （分割・削除・コピー）は対象が決まらないので出さない。伝票単位の表示から行う。
   */
  const singleOrderItem = unassignedItem?.isLaneGroup ? null : unassignedItem;

  // 未割当は「案件編集」、車両上のカードは「レーン管理」。どちらも対象 ID が無ければ無効化する。
  // 「地点単位」でまとめた未割当だけは案件が 1 つに決まらないので、まとまりの実体である
  // レーンを開く（案件編集は開く先が無く disabled のままになる）
  const editButton = (() => {
    if (selection.source === 'unassigned' && !selection.item.isLaneGroup) {
      // 解決関数が無いと常に disabled になる。「出るのに押せないボタン」を作らない
      if (!onEditOrder || !resolveShipmentOrderId) return null;
      const shipmentOrderId = resolveShipmentOrderId(selection.item.id);
      const canEdit = shipmentOrderId != null;
      const tooltip = canEdit
        ? '案件編集画面を開く'
        : 'この案件は編集できません';
      return (
        <Tooltip title={tooltip} arrow>
          {/* disabledReason は素の title 属性で出す (disabled 要素は MUI Tooltip の
              hover を拾えないため。ConvenienceDispatchToolbar.tsx:551 と同じ規則)。 */}
          <span>
            <IconButton
              size="small"
              onClick={canEdit && !disabledReason ? () => onEditOrder(shipmentOrderId) : undefined}
              aria-label="案件編集を開く"
              disabled={!canEdit || !!disabledReason}
              title={disabledReason}
            >
              <EditOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      );
    }
    if (!onEditLane) return null;
    const { laneId } = selection.source === 'unassigned' ? selection.item : selection.card;
    const canEdit = laneId != null;
    const tooltip = canEdit
      ? 'レーンの編集画面を開く'
      : 'この案件は対応するレーンがありません';
    return (
      <Tooltip title={tooltip} arrow>
        <span>
          <IconButton
            size="small"
            onClick={canEdit && !disabledReason ? () => onEditLane(laneId) : undefined}
            aria-label="レーン管理を開く"
            disabled={!canEdit || !!disabledReason}
            title={disabledReason}
          >
            <EditOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  })();

  const panel = (
    <Box
      sx={{
        position: 'absolute',
        width: PANEL_WIDTH,
        overflowY: 'auto',
        borderRadius: '12px',
        border: '1px solid hsl(var(--border))',
        bgcolor: 'hsl(var(--background))',
        boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        // 地図の右上に浮かせる配置。親要素からはみ出さないよう高さを内側で止める
        ...(placement === 'mapTopRight'
          // top を 16 でなく 44 にしているのは、地図右上に表示フィルタ
          // (Map/MapDisplayFilter.tsx) が居るため。重ねるとフィルタが押せなくなる
          ? { top: 44, right: 16, zIndex: 1100, maxHeight: 'calc(100% - 60px)' }
          : null),
      }}
      style={placement === 'anchor' ? buildAnchorPositionStyle(clickRect) : undefined}
      onClick={placement === 'anchor' ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          px: 1.5,
          pt: 1.5,
          pb: 0.5,
          bgcolor: 'hsl(var(--background))',
        }}
      >
        {editButton}
        {/* 候補が空のまま描くと、押しても見出しだけの空 Popover が開く。
            呼び出し側は移動元を解決できないとき空を返す (buildMoveTargets.ts:29) */}
        {isDeliveryCard && onMoveToVehicle != null && (moveTargets?.length ?? 0) > 0 && (
          <>
            <Tooltip title="他車両へ配車" arrow>
              <IconButton
                size="small"
                onClick={(e) => onChangeMoveMenuAnchor(e.currentTarget)}
                aria-label="他車両へ配車"
              >
                <SwapHorizRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(moveMenuAnchor)}
              anchorEl={moveMenuAnchor}
              onClose={() => onChangeMoveMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { width: 240, maxHeight: 320, overflowY: 'auto', p: 0.5 } } }}
            >
              <Typography variant="caption" sx={{ px: 1, py: 0.75, display: 'block', color: 'text.secondary' }}>
                移動先の車両を選択
              </Typography>
              {(moveTargets ?? []).map((target) => (
                <MenuItem
                  key={target.rowId}
                  onClick={() => onMoveToVehicle(target.rowId)}
                  sx={{ fontSize: 13 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {target.label}
                    </Box>
                    <Box sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {target.subLabel}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Popover>
          </>
        )}
        {isDeliveryCard && onResetToUnassigned != null && (
          <Tooltip title="未割り当てに戻す" arrow>
            <IconButton
              size="small"
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '未割当に戻す',
                });
                onResetToUnassigned();
              }}
              aria-label="未割り当てに戻す"
              disabled={!!disabledReason}
              title={disabledReason}
            >
              <RestartAltRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {/* 分割済みの案件は再分割できない（地図画面と同様）。復元してから分割する */}
        {singleOrderItem != null && onSplit != null && !singleOrderItem.wasSplit && (
          // 理由は Tooltip に載せ、span で包む。直下の子に素の title を付けると MUI が
          // 警告を出し、disabled な要素は hover を拾わないので Tooltip も出ない。
          // describeChild は読み上げ名をボタンの aria-label に保つため
          <Tooltip title={disabledReason || '伝票を分割'} arrow describeChild>
            <span>
              <IconButton
                size="small"
                onClick={onSplit}
                aria-label="伝票を分割"
                disabled={!!disabledReason}
              >
                <CallSplitRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {singleOrderItem != null && onDelete != null && (
          <Tooltip title="案件を削除" arrow>
            <IconButton
              size="small"
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '未割当案件 削除',
                });
                onDelete();
              }}
              aria-label="案件を削除"
              sx={{ color: 'hsl(var(--destructive))' }}
              disabled={!!disabledReason}
              title={disabledReason}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {/* 旧地図配車の「コピー」(V3Plans presenters/OrderPresenter.tsx:118-125)。
            同じ内容の案件をもう 1 件作る。未割当案件にだけ意味がある */}
        {singleOrderItem != null && onCopy && (
          <Tooltip title="案件をコピー" arrow>
            <IconButton
              size="small"
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '未割当案件 コピー',
                });
                onCopy();
              }}
              aria-label="案件をコピー"
              disabled={!!disabledReason}
              title={disabledReason}
            >
              <ContentCopyRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onClose} aria-label="閉じる">
          <CloseRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <DispatchDetailPanel
          selection={selection}
          screenName={screenName}
          hasDuplicate={hasDuplicate}
          geoAlert={geoAlert}
          onGeoUpdate={onGeoUpdate}
          disabledReason={disabledReason}
        />
      </Box>
    </Box>
  );

  // 地図では全画面オーバーレイを敷けない（地図のホバーとクリックを奪う）ため、
  // 外枠ごと placement で分ける
  if (placement !== 'anchor') return panel;

  return (
    <Box
      data-testid="detail-panel-backdrop"
      sx={{ position: 'fixed', inset: 0, zIndex: 1300 }}
      onClick={onClose}
    >
      {panel}
    </Box>
  );
}
